import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/useToast';
import { getApiErrorMessage } from '../../utils/apiErrors';
import type { PendingActionItem } from '../../api/pendingActions';
import type { RowDecision } from './PendingActionRow';
import { dispatchDecision, isOneTap } from './dispatch';
import { notifyPendingActionsChanged } from './usePendingActions';

/**
 * The one decision sequence every surface shares: confirm → dispatch → toast →
 * refetch → broadcast. Lifted out of PendingActionsPanel so the notification
 * bell runs the identical path instead of a second, drifting copy of it.
 */

export interface UsePendingActionDecisionOptions {
  /** Silent refetch of the host's own list once the action settles. */
  onChanged: () => Promise<void> | void;
  /** Ran after a decision the server accepted, before the refetch. */
  onResolved?: (item: PendingActionItem, positive: boolean) => void;
}

export interface UsePendingActionDecisionResult {
  /** The (item, decision) awaiting confirmation; null = no dialog open. */
  decisionTarget: { item: PendingActionItem; positive: boolean } | null;
  /** True while a decision is in flight (locks every dismissal path together). */
  submitting: boolean;
  /** The in-flight decision on a given row, or null. */
  busyFor: (item: PendingActionItem) => RowDecision;
  /** Row entry point: opens the confirm dialog, or fires directly when one-tap. */
  requestDecision: (item: PendingActionItem, positive: boolean) => void;
  /** Dialog confirm handler. */
  confirmDecision: (note: string) => void;
  /** Dialog dismiss handler. */
  closeDecision: () => void;
  /** Escape hatch for hosts that already know the note (bulk flows). */
  runDecision: (item: PendingActionItem, positive: boolean, note: string) => Promise<void>;
  /** Toast plumbing for the host's <ToastRenderer />. */
  toast: ReturnType<typeof useToast>['toast'];
  showToast: ReturnType<typeof useToast>['showToast'];
  dismissToast: ReturnType<typeof useToast>['dismissToast'];
}

export const pendingActionKey = (item: Pick<PendingActionItem, 'source' | 'id'>) => `${item.source}:${item.id}`;

export function usePendingActionDecision({
  onChanged,
  onResolved,
}: UsePendingActionDecisionOptions): UsePendingActionDecisionResult {
  const { t } = useTranslation('requests');
  const { toast, showToast, dismissToast } = useToast();

  const [decisionTarget, setDecisionTarget] = useState<{ item: PendingActionItem; positive: boolean } | null>(null);
  const [busy, setBusy] = useState<{ key: string; decision: RowDecision } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const runDecision = useCallback(
    async (item: PendingActionItem, positive: boolean, note: string) => {
      setBusy({ key: pendingActionKey(item), decision: positive ? 'positive' : 'negative' });
      setSubmitting(true);
      try {
        const result = await dispatchDecision(item, positive, note);
        if (result.errors.length === 0) {
          showToast(t('Done, removed from your pending actions'), 'success');
          onResolved?.(item, positive);
        } else {
          // Already-resolved tolerance: surface the domain error, then refetch —
          // if someone else handled it, the item simply disappears.
          showToast(getApiErrorMessage(result, t, t('Something went wrong')), 'error');
        }
      } catch (err) {
        showToast(getApiErrorMessage(err, t, t('Something went wrong')), 'error');
      }
      setDecisionTarget(null);
      await onChanged();
      // Everything else showing this queue (badge, bell, dashboard card) refreshes now.
      notifyPendingActionsChanged();
      setBusy(null);
      setSubmitting(false);
    },
    [onChanged, onResolved, showToast, t],
  );

  const requestDecision = useCallback(
    (item: PendingActionItem, positive: boolean) => {
      if (isOneTap(item, positive)) {
        runDecision(item, positive, '');
        return;
      }
      setDecisionTarget({ item, positive });
    },
    [runDecision],
  );

  const confirmDecision = useCallback(
    (note: string) => {
      if (decisionTarget) runDecision(decisionTarget.item, decisionTarget.positive, note);
    },
    [decisionTarget, runDecision],
  );

  const closeDecision = useCallback(() => setDecisionTarget(null), []);

  const busyFor = useCallback(
    (item: PendingActionItem): RowDecision => (busy?.key === pendingActionKey(item) ? busy.decision : null),
    [busy],
  );

  return {
    decisionTarget,
    submitting,
    busyFor,
    requestDecision,
    confirmDecision,
    closeDecision,
    runDecision,
    toast,
    showToast,
    dismissToast,
  };
}
